import "./App.css";
import React from "react";

import Login from "./Login";
import useAuthRedirect from "./useAuthRedirect";

import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Dashboard from "./Dashboard";
import Events from "./Events";
import { GlobalProvider } from "./context/GlobalContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import Home from "./Home";
import EventForm from "./Events/EventForm";
import Settings from "./Settings/Settings";
import { ToastContainer } from "react-toastify";
import AdminUsers from "./Users";
import Hosts from "./Events/Hosts";
import HostForm from "./Events/Hosts/HostForm";
import PrivateAdminRoute from "./privateAdminRoute";
import ProfileSetup from "./ProfileSetup";
import AdminAgent from "./AdminAgent";
import Conversations from "./Conversations";
import GistAIConversations from "./GistAIConversations";
import Arbitration from "./Arbitration";
import Issues from "./Issues";
import Feedback from "./Feedback";
import Coupons from "./Coupons";
import GistAiLauncher from "./GistAiLauncher";
import Payouts from "./Admin/Payouts";
import Regions from "./Admin/Regions";
import Kpis from "./Kpis";
import HostApplications from "./HostApplications";
import HostNudges from "./Admin/HostNudges";
import { capturePageview } from "./analytics";

const AuthHandler = () => {
  useAuthRedirect();
  return null;
};

// Explicit page views on route change (GIS-215). `capture_pageview` is off in
// posthog.init because CRA + React Router means the automatic one fires once on
// load and never again.
//
// capturePageview() drops anything that is not a host-facing route, so this can
// stay unconditional — the super-admin exclusion lives in analytics/routes.js
// rather than being re-implemented at every mount point.
//
// Keyed on pathname only. The panel puts view mode, filters and tab state in
// component state rather than the query string, so `location.search` changes are
// rare, and re-firing on `location.key` would double-count a redirect.
const PageviewTracker = () => {
  const location = useLocation();
  React.useEffect(() => {
    capturePageview(location.pathname);
  }, [location.pathname]);
  return null;
};

// ToastContainer themed to match the active light/dark theme.
const ThemedToastContainer = () => {
  const { resolvedTheme } = useTheme();
  return <ToastContainer theme={resolvedTheme === "dark" ? "dark" : "light"} />;
};

function App() {
  return (
    <ThemeProvider>
      <GlobalProvider>
        <ThemedToastContainer />
        <Router>
        <AuthHandler />
        <PageviewTracker />
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/profile-setup" element={<ProfileSetup />} />
          <Route
            path="/home"
            element={
              <Home>
                <Dashboard />
              </Home>
            }
          />
          <Route
            path="/events"
            element={
              <Home>
                <Events />
              </Home>
            }
          />
          <Route
            path="/events/completed"
            element={
              <Home>
                <Events tab="completed" />
              </Home>
            }
          />
          <Route
            path="/events/cancelled"
            element={
              <Home>
                <Events tab="cancelled" />
              </Home>
            }
          />
          <Route
            path="/events/suggestions"
            element={
              <Home>
                <Events tab="suggestions" />
              </Home>
            }
          />
          <Route
            path="/events/hosts"
            element={
              <PrivateAdminRoute>
                <Home>
                  <Events tab="hosts" />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <Home>
                <Settings />
              </Home>
            }
          />
          <Route
            path="/events/new"
            element={
              <Home>
                <EventForm />
              </Home>
            }
          />
          <Route
            path="/events/:eventID/edit"
            element={
              <Home>
                <EventForm />
              </Home>
            }
          />
          <Route
            path="/users"
            element={
              <Home>
                <AdminUsers />
              </Home>
            }
          />
          <Route
            path="/events/requests"
            element={
              <PrivateAdminRoute>
                <Home>
                  <Events tab="requests" />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/hosts"
            element={
              <PrivateAdminRoute>
                <Home>
                  <Hosts />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/hosts/:vendorID/edit"
            element={
              <PrivateAdminRoute>
                <Home>
                  <HostForm />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/admin-agent"
            element={
              <PrivateAdminRoute>
                <Home>
                  <AdminAgent />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/conversations"
            element={
              <PrivateAdminRoute>
                <Home>
                  <Conversations />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/gist-ai"
            element={
              <PrivateAdminRoute>
                <Home>
                  <GistAIConversations />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/arbitration"
            element={
              <PrivateAdminRoute>
                <Home>
                  <Arbitration />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/issues"
            element={
              <PrivateAdminRoute>
                <Home>
                  <Issues />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/feedback"
            element={
              <PrivateAdminRoute>
                <Home>
                  <Feedback />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/gist-ai-launcher"
            element={
              <PrivateAdminRoute>
                <Home>
                  <GistAiLauncher />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/coupons"
            element={
              <PrivateAdminRoute>
                <Home>
                  <Coupons />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/payouts"
            element={
              <PrivateAdminRoute>
                <Home>
                  <Payouts />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/regions"
            element={
              <PrivateAdminRoute>
                <Home>
                  <Regions />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/host-nudges"
            element={
              <PrivateAdminRoute>
                <Home>
                  <HostNudges />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/host-applications"
            element={
              <PrivateAdminRoute>
                <Home>
                  <HostApplications />
                </Home>
              </PrivateAdminRoute>
            }
          />
          <Route
            path="/kpis"
            element={
              <PrivateAdminRoute>
                <Home>
                  <Kpis />
                </Home>
              </PrivateAdminRoute>
            }
          />
          {/* Payout settings were merged into the Payouts page (Settings button). */}
          <Route path="/payout-config" element={<Navigate to="/payouts" replace />} />
        </Routes>
        </Router>
      </GlobalProvider>
    </ThemeProvider>
  );
}

export default App;
